(ns kyber.events.pdf-viewer
  (:require [re-frame.core :as re-frame]
            [goog.object :as gobj]

            [kyber.events.utils :refer [base-uri kyber-event-fx]]
            [kyber.subscriptions.authentication :refer [session-token]]
            [kyber.utils :refer [debounce]]

            [kyber.subscriptions.pdf-viewer :refer [get-pdf-viewer]]
            [kyber.db.pdf-viewer :refer [initial-state]]

            [kyber.lib.pdftron.core :as pdftron-utils]))

(defn- get-initial-pdftron-viewer-state
  [_]
  {:active-tool :AnnotationEdit
   :zoom-level nil
   :max-zoom-level 2
   :min-zoom-level 0.5
   :search-enabled false
   :thumbnails-enabled false})

(defn- get-highlighted-el
  []
  (first
   (remove #(or (-> % .-classList (.contains "begin"))
                (-> % .-classList (.contains "end")))
           (.querySelectorAll js/document ".highlighted,.selected"))))

(defn- get-search-result-spot
  [results result-number]
  (:spot
   (reduce-kv
    (fn [{:keys [spot] :as acc} page-idx page]
      (if spot
        acc
        (reduce
         (fn [{:keys [current-result spot] :as acc} spot-value]
           (if spot
             acc
             (cond-> (update acc :current-result inc)
               (= (inc current-result) result-number)
               (assoc :spot [(inc page-idx) spot-value]))))
         acc
         page)))
    {:current-result -1
     :spot nil}
    results)))

(defn- get-most-visible-page
  [pages]
  (let [parent-container-height (-> pages
                                    first
                                    .-parentNode
                                    .-parentNode
                                    .getBoundingClientRect
                                    .-height)

        page-a-y (-> pages
                     first
                     .getBoundingClientRect
                     .-y
                     js/Math.abs)]

    (if (< page-a-y (/ parent-container-height 2))
      (first pages)
      (last pages))))

(defn- update-search-navigation
  [{:keys [db result search-results search-api ecid event-name]}]
  (let [next-result {:result result
                     :spot (get-search-result-spot search-results result)}
        [page offset] (:spot next-result)]
    {:db (-> db
             (assoc-in [:pdf-viewer :current-page] page)
             (assoc-in [:pdf-viewer :selected-search-result] next-result)
             (assoc-in [:pdf-viewer :lock-scroll-update] true))
     :dispatch-n [[:pdf-viewer/go-to-page {:page page
                                           :offset offset}]
                  [:analytics/save-datapoint
                   {:page page
                    :ecid ecid
                    :result result
                    :event event-name
                    :event-version 0}]]
     :pdf-viewer/update-highlighted-text
     (merge next-result
            {:search-results search-results
             :search-api search-api})}))

(defn- is-in-viewport
  [^js node]
  (let [rect (.getBoundingClientRect node)
        doc-el (.-documentElement js/document)
        doc-rect (.getBoundingClientRect doc-el)

        ;; Constant: Height of the viewport
        client-height (or (.-innerHeight js/window)
                          (.-clientHeight doc-el))

        ;; Dynamic: Current overall viewport-y-position
        viewport-y-top (- (or (.-pageYOffset js/window)
                              (.-scrollTop doc-el))
                          (or (.-clientTop doc-el) 0))
        viewport-y-bottom (+ viewport-y-top client-height)

        ;; Constant: y distance to the top of the page
        page-top (- (.-top rect) (.-top doc-rect))
        page-bottom (- (.-bottom rect) (.-top doc-rect))]

    (or (and (>= page-top viewport-y-top)
             (<= page-top viewport-y-bottom))
        (and (>= page-bottom viewport-y-top)
             (<= page-bottom viewport-y-bottom))
        (and (< page-top viewport-y-top)
             (> page-bottom viewport-y-bottom)))))

(defn-  update-search-results-dispatch
  [dispatch]
  #(dispatch [:pdf-viewer/update-search-results]))

(defn- debounced-update-search-results-dispatch
  [dispatch]
  (debounce (update-search-results-dispatch dispatch) 250))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Initialize PDF Viewer
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-initialize
    {:description
     "Request to load a PDF document has been initialized"
     :changelog
     [{:date "13MAY18"
       :event-version 0}]}))

(defn initialize
  [{:keys [db]} [event-name {:keys [resource ecid] :as props}]]
  (let [url (:resource/url resource)
        formatted-url (str (base-uri db url)
                           "?token="
                           (session-token db))]


    {:db (update db :pdf-viewer #(assoc initial-state
                                        :ecid ecid
                                        :url url))
     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0
                                              :ecid ecid}]]
     :pdf-viewer/load-document
     (assoc props :url formatted-url)}))

(kyber-event-fx :pdf-viewer/initialize initialize)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Document Loaded
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-document-loaded
    {:description
     "PDF document has loaded"
     :changelog
     [{:date "13MAY18"
       :event-version 0}]}))

(defn document-loaded
  [{:keys [db]} [event-name document]]
  (let [{:keys [container
                document-api
                search-api
                viewer-api
                total-pages
                current-page
                current-scale]}
        document]
    {:db (update db :pdf-viewer #(merge % {:container container
                                           :current-page current-page
                                           :current-scale current-scale
                                           :document-api document-api
                                           :document-loaded true
                                           :search-api search-api
                                           :total-pages total-pages
                                           :viewer-api viewer-api}))
     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0}]]}))

(kyber-event-fx :pdf-viewer/document-loaded document-loaded)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Close PDF
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-close
    {:description
     "Close PDF"
     :changelog
     [{:date "13MAY18"
       :event-version 0}]}))

(defn close
  [{:keys [db]} [event-name]]
  (let [{:keys [ecid]} (get-pdf-viewer db nil)]
    {:db (assoc db :pdf-viewer initial-state)
     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0
                                              :ecid ecid}]]}))

(kyber-event-fx :pdf-viewer/close close)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Go To Page
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-go-to-page
    {:description
     "Triggers PDF navigation event."
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data {:offset :number
              :page :number}}]}))

(defn go-to-page
  [{:keys [db]} [event-name page]]
  (let [{:keys [ecid]} (get-pdf-viewer db nil)]
    {:db db
     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0
                                              :offset (:offset page)
                                              :page (:page page)
                                              :ecid ecid}]]
     :pdf-viewer/go-to-page (assoc page
                                   :viewer-api
                                   (get-in db [:pdf-viewer :viewer-api]))}))

(kyber-event-fx :pdf-viewer/go-to-page go-to-page)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Next Page
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-next-page
    {:description
     "Navigates to the next page. Page is the navigated to page."
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data {:page :number}}]}))

(defn next-page
  [{:keys [db]} [event-name]]
  (let [{:keys [current-page total-pages ecid]}
        (get-pdf-viewer db nil)]
    (if (< current-page total-pages)
      {:db (-> db
               (update-in [:pdf-viewer :current-page] inc)
               (assoc-in [:pdf-viewer :lock-scroll-update] true))
       :dispatch-n [[:analytics/save-datapoint {:event event-name
                                                :event-version 0
                                                :page (inc current-page)
                                                :ecid ecid}]
                    [:pdf-viewer/go-to-page {:page (inc current-page)}]]}
      {:db db})))

(kyber-event-fx :pdf-viewer/next-page next-page)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Previous Page
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-previous-page
    {:description
     "Navigates to the previous page. Page is the navigated to page."
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data {:page :number}}]}))

(defn previous-page
  [{:keys [db]} [event-name]]
  (let [{:keys [current-page ecid]}
        (get-pdf-viewer db nil)]
    (if (> current-page 1)
      {:db (-> db
               (update-in [:pdf-viewer :current-page] dec)
               (assoc-in [:pdf-viewer :lock-scroll-update] true))
       :dispatch-n [[:analytics/save-datapoint {:event event-name
                                                :event-version 0
                                                :page (dec current-page)
                                                :ecid ecid}]
                    [:pdf-viewer/go-to-page {:page (dec current-page)}]]}
      {:db db})))

(kyber-event-fx :pdf-viewer/previous-page previous-page)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Search Text
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

;; NOTE: This event is tracked within the PDF-Viewer component.
;;       The component debounces the analytics associated with
;;       this event.
(when ^boolean js/goog.DEBUG
  (def event-doc-search-text
    {:description
     "Triggers search of the PDF document"
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data {:search :string}}]}))

(defn search-text
  [{:keys [db]} [_event-name text]]
  (let [{:keys [search-api]} (get-pdf-viewer db nil)]
    {:db db
     :pdf-viewer/search-text {:query text
                              :search-api search-api}}))

(kyber-event-fx :pdf-viewer/search-text search-text)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Update Search Results
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-search-results
    {:description
     "Update search results"
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data {:total-search-results :number
              :search :string}}]}))

(defn update-search-results
  [{:keys [db]} [event-name _]]
  (let [{:keys [search-api ecid search-results]}
        (get-pdf-viewer db nil)

        matches
        (js->clj (.-pageMatches search-api))

        first-result
        {:result 0
         :spot (get-search-result-spot matches 0)}

        [_page _offset]
        (:spot first-result)

        total-search-results
        (count (flatten matches))

        search
        (-> search-api .-state .-query)]

    (cond-> {:db db}
      ;; Set on changes to search
      (not= matches search-results)
      (-> (assoc-in [:db :pdf-viewer :search-results]
                    matches)
          (assoc-in [:db :pdf-viewer :total-search-results]
                    total-search-results)
          (assoc-in [:db :pdf-viewer :selected-search-result]
                    first-result)
          (assoc :dispatch-n [[:analytics/save-datapoint
                               {:total-search-results total-search-results
                                :search search
                                :ecid ecid
                                :event event-name
                                :event-version 0}]])))))

(kyber-event-fx :pdf-viewer/update-search-results update-search-results)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Next Result
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-next-result
    {:description
     "Navigate to the next search result"
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data {:page :number
              :result :number}}]}))

(defn next-result
  [{:keys [db]} [event-name]]
  (let [{:keys [search-api
                search-results
                selected-search-result
                total-search-results
                ecid]}
        (get-pdf-viewer db nil)

        {:keys [result]}
        selected-search-result]

    (if (< (inc result) total-search-results)
      (update-search-navigation {:db db
                                 :event-name event-name
                                 :result (inc result)
                                 :search-results search-results
                                 :search-api search-api
                                 :ecid ecid})
      {:db db})))


(kyber-event-fx :pdf-viewer/next-result next-result)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Previous Result
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-previous-result
    {:description
     "Navigate to the previous search result"
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data {:page :number
              :result :number}}]}))

(defn previous-result
  [{:keys [db]} [event-name]]
  (let [{:keys [search-api
                search-results
                selected-search-result
                ecid]}
        (get-pdf-viewer db nil)

        {:keys [result]}
        selected-search-result]
    (if (pos? result)
      (update-search-navigation {:db db
                                 :event-name event-name
                                 :result (dec result)
                                 :search-results search-results
                                 :search-api search-api
                                 :ecid ecid})
      {:db db})))

(kyber-event-fx :pdf-viewer/previous-result previous-result)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Scroll Page
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-scroll-page
    {:description
     "Page is scrolling"
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data {:page :number}}]}))

(defn scroll-page
  [{:keys [db]} [event-name]]
  (let [{:keys [^js viewer-api lock-scroll-update current-page ecid]}
        (get-pdf-viewer db nil)

        pages-in-viewport
        (->> viewer-api
             .-viewer
             .-childNodes
             (filter is-in-viewport))

        first-page-in-viewport
        (first pages-in-viewport)

        scroll-page
        (-> ^js first-page-in-viewport
            .-dataset
            .-pageNumber
            js/parseInt)

        active-page
        (condp = (count pages-in-viewport)
          1 first-page-in-viewport
          2 (get-most-visible-page pages-in-viewport)
          (second pages-in-viewport))

        active-page-number
        (-> ^js active-page
            .-dataset
            .-pageNumber
            js/parseInt)]

    (cond-> {:db db}
      (not lock-scroll-update)
      (assoc-in [:db :pdf-viewer :current-page] active-page-number)

      (and (not lock-scroll-update)
           (not= current-page scroll-page))
      (assoc :dispatch [:analytics/save-datapoint
                        {:page active-page-number
                         :event event-name
                         :event-version 0
                         :ecid ecid}]))))

(kyber-event-fx :pdf-viewer/scroll-page scroll-page)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Unlock Scroll Update
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-unlock-scroll-update
    {:description
     "Unlocks PDF Viewer scrolling"
     :changelog
     [{:date "13MAY18"
       :event-version 0}]}))

(defn unlock-scroll-update
  [{:keys [db]} [event-name]]
  (let [{:keys [ecid]} (get-pdf-viewer db nil)]
    {:db (assoc-in db [:pdf-viewer :lock-scroll-update] false)
     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0
                                              :ecid ecid}]]}))

(kyber-event-fx :pdf-viewer/unlock-scroll-update unlock-scroll-update)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Set Scale To Fit
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-update-scale
    {:description
     "Updates the PDF scale to fit the current viewport"
     :changelog
     [{:date "13MAY18"
       :event-version 0}]}))

(defn set-scale-to-fit
  [{:keys [db]} [event-name]]
  (let [{:keys [ecid]} (get-pdf-viewer db nil)]
    {:db db
     :pdf-viewer/set-scale-to-fit (get-pdf-viewer db nil)
     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0
                                              :ecid ecid}]]}))

(kyber-event-fx :pdf-viewer/set-scale-to-fit set-scale-to-fit)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Update Scale
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-update-scale
    {:description
     "Updates the pdf scale"
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data {:scale :number}}]}))

(defn update-scale
  [{:keys [db]} [event-name scale]]
  (let [{:keys [current-scale viewer-api ecid]}
        (get-pdf-viewer db nil)

        max-scale
        2.6

        min-scale
        0.2

        step
        0.1

        updated-scale
        (if (pos? (:dir scale))
          (+ current-scale step)
          (- current-scale step))]

    (if (or (< updated-scale min-scale)
            (> updated-scale max-scale))
      {:db db}
      {:db (assoc-in db [:pdf-viewer :current-scale] updated-scale)
       :pdf-viewer/update-scale {:viewer-api viewer-api
                                 :scale updated-scale}
       :dispatch [:analytics/save-datapoint {:event event-name
                                             :ecid ecid
                                             :event-version 0
                                             :scale updated-scale}]})))

(kyber-event-fx :pdf-viewer/update-scale update-scale)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Set Scale
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-set-scale
    {:description
     "Sets the current scale of the pdf viewer for application reference"
     :changelog
     [{:date "13MAY18"
       :event-version 0
       :data {:scale :number}}]}))

(defn set-scale
  [{:keys [db]} [event-name scale]]
  (let [{:keys [ecid]} (get-pdf-viewer db nil)]
    {:db (assoc-in db [:pdf-viewer :current-scale] scale)
     :dispatch-n [[:analytics/save-datapoint
                   {:event event-name
                    :event-version 0
                    :ecid ecid
                    :scale scale}]]}))

(kyber-event-fx :pdf-viewer/set-scale set-scale)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Scroll To Highlighted Text
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-scroll-to-highlighted-text
    {:description
     "Scrolls the viewer to some highlighted text"
     :changelog
     [{:date "13MAY18"
       :event-version 0}]}))

(defn scroll-to-highlighted-text
  [{:keys [db]} [event-name]]
  (let [{:keys [viewer-api ecid]}
        (get-pdf-viewer db nil)]
    {:db db
     :pdf-viewer/scroll-to-highlighted-text viewer-api
     :dispatch-n [[:analytics/save-datapoint
                   {:event event-name
                    :event-version 0
                    :ecid ecid}]]}))

(kyber-event-fx :pdf-viewer/scroll-to-highlighted-text
                scroll-to-highlighted-text)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Effects
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn fx-load-document
  [dispatch]
  (fn [{:keys [url container]}]
    (set! (.-externalLinkTarget js/PDFJS) (.. js/PDFJS -LinkTarget -BLANK))
    (-> js/PDFJS
        (.getDocument url)
        .-promise
        (.then
         (fn [^js document-api]
           (let [viewer-api
                 (js/PDFJS.PDFViewer.
                  #js {:container container})

                 search-api
                 (js/PDFJS.PDFFindController.
                  #js {:pdfViewer viewer-api})

                 total-pages (-> document-api
                                 .-pdfInfo
                                 (js->clj :keywordize-keys true)
                                 :numPages)]
             ;; 0 = Found
             ;; 1 = Not Found
             (set! (.-onUpdateState search-api)
                   #(when (or (zero? %)
                              (= 1 %))
                      ((debounced-update-search-results-dispatch dispatch))))

             (set! (.-onUpdateResultsCount search-api)
                   (debounced-update-search-results-dispatch dispatch))

             (.setFindController viewer-api search-api)
             (.setDocument viewer-api document-api)

             (-> viewer-api
                 (.-onePageRendered)
                 (.then (fn []
                          (dispatch [:pdf-viewer/document-loaded
                                     {:container container
                                      :document-api document-api
                                      :search-api search-api
                                      :viewer-api viewer-api
                                      :current-page 1
                                      :total-pages total-pages}])
                          (dispatch [:pdf-viewer/set-scale-to-fit]))))))))))

(re-frame/reg-fx :pdf-viewer/load-document
                 (fx-load-document re-frame/dispatch))

(defn fx-set-scale-to-fit
  [dispatch]
  (fn [{:keys [^js viewer-api container]}]
    (let [pdf-width
          (-> viewer-api
              (.getPageView 0)
              .-viewport
              .-width)

          container-offset-width
          (.-offsetWidth container)

          scale
          (js/Math.min
           1
           (* (/ container-offset-width pdf-width)
              ;; https://github.com/mozilla/pdf.js/issues/5628
              ;; Had to adjust to give a little more forgiving
              0.94))]

      (set! (.-currentScaleValue viewer-api) scale)

      (dispatch [:pdf-viewer/set-scale scale]))))

(re-frame/reg-fx :pdf-viewer/set-scale-to-fit
                 (fx-set-scale-to-fit re-frame/dispatch))

(defn fx-go-to-page
  [dispatch]
  (fn [{:keys [page ^js viewer-api]}]
    (let [page-el
          (-> viewer-api
              .-_pages
              js->clj
              (get (dec page))
              .-div)

          parent-el
          (-> page-el .-parentNode .-parentNode)]

      ;; TODO Take into account searched text offset
      (set! (.-scrollTop parent-el)
            (- (.-offsetTop page-el) 10))

      (.setTimeout js/window
                   #(dispatch [:pdf-viewer/unlock-scroll-update])
                   100))))

(re-frame/reg-fx :pdf-viewer/go-to-page
                 (fx-go-to-page re-frame/dispatch))

(defn fx-search-text
  [{:keys [query ^js search-api]}]
  (.executeCommand search-api "find"
                   (clj->js {:query query
                             :caseSensitive false
                             :highlightAll true
                             :phraseSearch true})))

(re-frame/reg-fx :pdf-viewer/search-text fx-search-text)

(defn fx-update-highlighted-text
  [dispatch]
  (fn [{:keys [spot search-results ^js search-api]}]
    (let [[page offset] spot
          page-idx (dec page)
          match-idx (.indexOf ^js (get search-results page-idx) offset)
          highlighted-el (get-highlighted-el)]

      (set! (.-selected search-api) (clj->js {:matchIdx match-idx
                                              :pageIdx page-idx}))

      (when highlighted-el
        (-> highlighted-el .-classList (.remove "selected")))

      (.updateMatch search-api)

      ;; Sometime pdfjs appears to not be applying the selected class
      ;; to the current selection
      (when (get-highlighted-el)
        (dispatch [:pdf-viewer/scroll-to-highlighted-text])))))

(re-frame/reg-fx :pdf-viewer/update-highlighted-text
                 (fx-update-highlighted-text re-frame/dispatch))

(defn fx-scroll-to-highlighted-text
  [_search-api _direction]
  (let [highlighted-text
        (get-highlighted-el)

        highlighted-line
        (.-parentElement highlighted-text)

        page
        (-> highlighted-line
            .-parentElement
            .-parentElement)

        container
        (-> page
            .-parentElement
            .-parentElement)]

    (set! (.-scrollTop container)
          (- (+ (.-offsetTop page)
                (.-offsetTop highlighted-line))
             40))))

(re-frame/reg-fx :pdf-viewer/scroll-to-highlighted-text
                 fx-scroll-to-highlighted-text)

(defn fx-update-scale
  [{:keys [viewer-api scale]}]
  (set! (.-currentScaleValue viewer-api) scale))

(re-frame/reg-fx :pdf-viewer/update-scale fx-update-scale)

;; PDF TRON EFFECTS

(defn- set-pdftron-viewer
  [{:keys [viewer-key] :as viewer-instance}]
  (swap! pdftron-utils/pdf-viewer-instances
         #(assoc % viewer-key viewer-instance)))

(re-frame/reg-fx :pdftron-viewer/set-viewer set-pdftron-viewer)

(defn fx-pdftron-load-document
  [dispatch]
  (fn [options]
    (pdftron-utils/init-web-viewer (assoc options :dispatch dispatch))))

(re-frame/reg-fx :pdftron-viewer/load-document
                 (fx-pdftron-load-document re-frame/dispatch))

(defn fx-pdftron-go-to-annotation
  [{:keys [xfdf-id apis]}]
  (let [{:keys [annotation-manager]} apis]
    (when (and (some? xfdf-id)
               (some? annotation-manager))
      (js-invoke annotation-manager
                 "jumpToAnnotation"
                 (js-invoke annotation-manager
                            "getAnnotationById"
                            xfdf-id)))))

(re-frame/reg-fx :pdftron-viewer/go-to-annotation fx-pdftron-go-to-annotation)

(defn fx-pdftron-go-to-page
  [{:keys [page apis]}]
  (let [{:keys [doc-viewer]} apis]
    (when (and (some? page)
               (some? doc-viewer))
      ;; Increment zero-indexed page for PDFTron
      (js-invoke doc-viewer "setCurrentPage" (inc page)))))

(re-frame/reg-fx :pdftron-viewer/go-to-page fx-pdftron-go-to-page)

(defn fx-pdftron-remove-annotation
  [{:keys [xfdf-id apis]}]
  (let [{:keys [doc-viewer annotation-manager]} apis]
    (when (and (some? xfdf-id)
               (some? doc-viewer))
      (when-let [annotation (js-invoke annotation-manager
                                       "getAnnotationById"
                                       xfdf-id)]
        ;; TODO Appears that removing annotations is only
        ;; part of the full API? Need to follow up with support
        (js-invoke annotation-manager "hideAnnotation" annotation)))))

(re-frame/reg-fx :pdftron-viewer/remove-annotation fx-pdftron-remove-annotation)

(defn fx-pdftron-on-tool-select
  [{:keys [apis tool-name]}]
  (let [{:keys [doc-viewer]} apis]
    (pdftron-utils/set-tool-mode doc-viewer tool-name)))

(re-frame/reg-fx :pdftron-viewer/on-tool-select fx-pdftron-on-tool-select)

(defn fx-pdftron-set-zoom
  [{:keys [apis zoom-level]}]
  (js-invoke (:doc-viewer apis) "zoomTo" zoom-level))

(re-frame/reg-fx :pdftron-viewer/set-zoom fx-pdftron-set-zoom)

(defn fx-pdftron-show-elements
  [{:keys [apis elements]}]
  ((:open-elements apis) elements))

(re-frame/reg-fx :pdftron-viewer/show-elements fx-pdftron-show-elements)

(defn fx-pdftron-hide-elements
  [{:keys [apis elements]}]
  ((:close-elements apis) elements))

(re-frame/reg-fx :pdftron-viewer/hide-elements fx-pdftron-hide-elements)

(defn fx-pdftron-show-thumbnail-button
  [{:keys [apis]}]
  ((:enable-elements apis) #js ["kyber--thumbnail-toggle"]))

(re-frame/reg-fx :pdftron-viewer/show-thumbnail-button
                 fx-pdftron-show-thumbnail-button)

(defn fx-pdftron-disable-features
  [{:keys [apis features]}]
  ((:disable-features apis) features))

(re-frame/reg-fx :pdftron-viewer/disable-features
                 fx-pdftron-disable-features)

;; PDF TRON EVENTS

(defn pdftron-disable-features
  [_ [_ {:keys [viewer-key features]}]]
  (let [{:keys [apis]} (pdftron-utils/get-pdftron-viewer viewer-key)]
    {:pdftron-viewer/disable-features {:features features
                                       :apis apis}}))

(kyber-event-fx :pdftron-viewer/disable-features pdftron-disable-features)

(when ^boolean js/goog.DEBUG
  (def event-doc-pdftron-on-tool-select
    {:description
     "An event was triggered to select a tool"
     :changelog
     [{:date "28FEB20"
       :event-version 0
       :data {:tool-name :string
              :viewer-key :string
              :current-page-index :number}}]}))

(defn pdftron-on-tool-select
  [{:keys [db]} [event-name {:keys [options tool-name]}]]
  (let [{:keys [viewer-key]} options
        {:keys [ecid apis]} (pdftron-utils/get-pdftron-viewer viewer-key)]
    {:db (assoc-in db [:pdftron-instances viewer-key :active-tool] tool-name)
     :dispatch-n [[:analytics/save-datapoint
                   {:event event-name
                    :event-version 0
                    :ecid ecid
                    :tool-name tool-name
                    :viewer-key viewer-key
                    :current-page-index
                    (pdftron-utils/get-current-page-index apis)}]]
     :pdftron-viewer/on-tool-select {:tool-name tool-name
                                     :apis apis}}))

(kyber-event-fx :pdftron-viewer/on-tool-select pdftron-on-tool-select)

(when ^boolean js/goog.DEBUG
  (def event-doc-pdftron-annotation-added
    {:description
     "Annotation was created"
     :changelog
     [{:date "28FEB20"
       :event-version 0
       :data {:viewer-key :string
              :current-page-index :number}}]}))

(defn pdftron-annotation-added
  [{:keys [db]} [event-name options annotation]]
  (let [{:keys [viewer-key]} options
        {:keys [ecid apis]} (pdftron-utils/get-pdftron-viewer viewer-key)]
    {:db (cond-> db
           (contains? #{:AnnotationCreateSticky
                        :AnnotationCreateTextHighlight}
                      (keyword (gobj/get annotation "ToolName")))
           (assoc-in [:pdftron-instances viewer-key :active-tool]
                     :AnnotationEdit))
     :dispatch-n [[:pdftron-viewer/on-tool-select
                   {:options options
                    :tool-name "AnnotationEdit"}]
                  [:analytics/save-datapoint
                   {:event event-name
                    :event-version 0
                    :ecid ecid
                    :viewer-key viewer-key
                    :current-page-index
                    (pdftron-utils/get-current-page-index apis)}]]}))

(kyber-event-fx :pdftron-viewer/annotation-added pdftron-annotation-added)

(when ^boolean js/goog.DEBUG
  (def event-doc-pdftron-go-to-page
    {:description
     "Triggers PDF navigation event."
     :changelog
     [{:date "13FEB20"
       :event-version 0
       :data {:page :number
              :viewer-key :string
              :current-page-index :number}}]}))

(defn pdftron-go-to-page
  [_ [event-name {:keys [page viewer-key]}]]
  (let [{:keys [ecid apis]} (pdftron-utils/get-pdftron-viewer viewer-key)]
    {:dispatch-n [[:analytics/save-datapoint
                   {:event event-name
                    :event-version 0
                    :page page
                    :ecid ecid
                    :viewer-key viewer-key
                    :current-page-index
                    (pdftron-utils/get-current-page-index apis)}]]
     :pdftron-viewer/go-to-page {:page page
                                 :apis apis}}))

(kyber-event-fx :pdftron-viewer/go-to-page pdftron-go-to-page)

(defn pdftron-set-thumbnail
  [{:keys [db]} [_ {:keys [viewer-key thumbnail-url]}]]
  {:db (assoc-in db [:pdftron-instances viewer-key :thumbnail] thumbnail-url)})

(kyber-event-fx :pdftron-viewer/set-thumbnail pdftron-set-thumbnail)

(defn pdftron-generate-thumbnail
  [_ [event-name {:keys [viewer-key]}]]
  (let [{:keys [apis]} (pdftron-utils/get-pdftron-viewer viewer-key)
        document (some-> (:doc-viewer apis) (js-invoke "getDocument"))]
    (when (some? document)
      ^js (.loadThumbnailAsync document 0
        (fn [thumb]
          ^js (.toBlob thumb
            #(re-frame/dispatch
              [:pdftron-viewer/set-thumbnail
               {:viewer-key viewer-key
                :thumbnail-url (.createObjectURL js/URL %)}]))))))
  {:dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0
                  :viewer-key viewer-key}]]})

(kyber-event-fx :pdftron-viewer/generate-thumbnail pdftron-generate-thumbnail)

(when ^boolean js/goog.DEBUG
  (def event-doc-pdftron-go-to-annotation
    {:description
     "Triggers PDF navigation event for an annotation."
     :changelog
     [{:date "19FEB20"
       :event-version 0
       :data {:xfdf-id :uuid
              :viewer-key :string
              :current-page-index :number}}]}))

(defn pdftron-go-to-annotation
  [_ [event-name {:keys [xfdf-id viewer-key]}]]
  (let [{:keys [ecid apis]} (pdftron-utils/get-pdftron-viewer viewer-key)]
    {:dispatch-n [[:analytics/save-datapoint
                   {:event event-name
                    :event-version 0
                    :xfdf-id xfdf-id
                    :ecid ecid
                    :viewer-key viewer-key
                    :current-page-index
                    (pdftron-utils/get-current-page-index apis)}]]
     :pdftron-viewer/go-to-annotation {:xfdf-id xfdf-id
                                       :apis apis}}))

(kyber-event-fx :pdftron-viewer/go-to-annotation pdftron-go-to-annotation)

(when ^boolean js/goog.DEBUG
  (def event-doc-pdftron-remove-annotation
    {:description
     "Triggers remove annotation event"
     :changelog
     [{:date "21FEB20"
       :event-version 0
       :data {:xfdf-id :uuid
              :viewer-key :string
              :current-page-index :number}}]}))

(defn pdftron-remove-annotation
  [_ [event-name {:keys [xfdf-id viewer-key]}]]
  (let [{:keys [ecid apis]} (pdftron-utils/get-pdftron-viewer viewer-key)]
    {:dispatch-n [[:analytics/save-datapoint
                   {:event event-name
                    :event-version 0
                    :xfdf-id xfdf-id
                    :ecid ecid
                    :viewer-key viewer-key
                    :current-page-index
                    (pdftron-utils/get-current-page-index apis)}]]
     :pdftron-viewer/remove-annotation {:xfdf-id xfdf-id
                                        :apis apis}}))

(kyber-event-fx :pdftron-viewer/remove-annotation pdftron-remove-annotation)

(when ^boolean js/goog.DEBUG
  (def event-doc-pdftron-set-zoom
    {:description
     "Set zoom to value"
     :changelog
     [{:date "02MAR20"
       :event-version 0
       :data {:viewer-key :string
              :current-page-index :number
              :current-zoom :number
              :next-zoom :number}}]}))

(defn pdftron-set-zoom
  [{:keys [db]} [event-name
                 {:keys [viewer-key direction max-zoom-level min-zoom-level]}]]
  (let [{:keys [ecid apis]}
        (pdftron-utils/get-pdftron-viewer viewer-key)

        {:keys [zoom-level]}
        (get-in db [:pdftron-instances viewer-key])

        zoom-change-by
        0.25

        next-val
        (if (= direction :in)
          (min (+ zoom-level zoom-change-by) max-zoom-level)
          (max (- zoom-level zoom-change-by) min-zoom-level))]

    {:db (assoc-in db [:pdftron-instances viewer-key :zoom-level] next-val)
     :dispatch-n [[:analytics/save-datapoint
                   {:event event-name
                    :event-version 0
                    :ecid ecid
                    :current-zoom zoom-level
                    :next-zoom next-val
                    :viewer-key viewer-key
                    :current-page-index
                    (pdftron-utils/get-current-page-index apis)}]]
     :pdftron-viewer/set-zoom {:apis apis
                               :zoom-level next-val}}))

(kyber-event-fx :pdftron-viewer/set-zoom pdftron-set-zoom)

(when ^boolean js/goog.DEBUG
  (def event-doc-pdftron-update-visible-pages
    {:description
     "Updates the visible pages"
     :changelog
     [{:date "02MAR20"
       :event-version 0
       :data {:viewer-key :string
              :current-page-index :number}}]}))

(defn pdftron-update-visible-pages
  [{:keys [db]} [event-name {:keys [viewer-key visible-pages]}]]
  (let [{:keys [ecid apis]} (pdftron-utils/get-pdftron-viewer viewer-key)]
    {:db (assoc-in db
                   [:pdftron-instances viewer-key :visible-pages]
                   visible-pages)
     :dispatch-n [[:analytics/save-datapoint
                   (cond-> {:event event-name
                            :event-version 0
                            :ecid ecid
                            :viewer-key viewer-key}
                     (some? apis)
                     (assoc
                      :current-page-index
                      (pdftron-utils/get-current-page-index apis)))]]}))

(kyber-event-fx :pdftron-viewer/update-visible-pages
                pdftron-update-visible-pages)

(when ^boolean js/goog.DEBUG
  (def event-doc-pdftron-update-annotations-loaded
    {:description
     "Is set when annotations have been loaded"
     :changelog
     [{:date "17NOV20"
       :event-version 0
       :data {}}]}))

(defn pdftron-update-annotations-loaded
  [{:keys [db]} [event-name {:keys [viewer-key]}]]
  (let [{:keys [ecid]} (pdftron-utils/get-pdftron-viewer viewer-key)]
    {:db (assoc-in db
                   [:pdftron-instances viewer-key :annotation-layer-loaded]
                   true)
     :dispatch-n [[:analytics/save-datapoint
                   (cond-> {:event event-name
                            :event-version 0
                            :ecid ecid
                            :viewer-key viewer-key})]]}))

(kyber-event-fx :pdftron-viewer/update-annotations-loaded
                pdftron-update-annotations-loaded)

(when ^boolean js/goog.DEBUG
  (def event-doc-pdftron-page-scroll
    {:description
     "The page has been scrolled"
     :changelog
     [{:date "04SEP20"
       :event-version 0
       :data {:viewer-key :string
              :current-page-index :number}}]}))

(defn pdftron-page-scroll
  [_ [event-name {:keys [viewer-key]}]]
  (let [{:keys [ecid apis]} (pdftron-utils/get-pdftron-viewer viewer-key)]
    {:dispatch [:analytics/save-datapoint
                {:event event-name
                 :event-version 0
                 :ecid ecid
                 :viewer-key viewer-key
                 :current-page-index
                 (pdftron-utils/get-current-page-index apis)}]}))

(kyber-event-fx :pdftron-viewer/page-scroll
                pdftron-page-scroll)

(when ^boolean js/goog.DEBUG
  (def event-doc-pdftron-page-number-updated
    {:description
     "The page has been updated"
     :changelog
     [{:date "04SEP20"
       :event-version 0
       :data {:viewer-key :string
              :current-page-index :number}}]}))

(defn pdftron-page-number-updated
  [_ [event-name {:keys [viewer-key]}]]
  (let [{:keys [ecid apis]} (pdftron-utils/get-pdftron-viewer viewer-key)]
    {:dispatch [:analytics/save-datapoint
                {:event event-name
                 :event-version 0
                 :ecid ecid
                 :viewer-key viewer-key
                 :current-page-index
                 (pdftron-utils/get-current-page-index apis)}]}))

(kyber-event-fx :pdftron-viewer/page-number-updated
                pdftron-page-number-updated)

(when ^boolean js/goog.DEBUG
  (def event-doc-pdftron-zoom-in
    {:description
     "Zoom in event"
     :changelog
     [{:date "02MAR20"
       :event-version 0
       :data {:viewer-key :string
              :current-page-index :number}}]}))

(defn pdftron-zoom-in
  [_ [event-name {:keys [viewer-key max-zoom-level]}]]
  (let [{:keys [ecid apis]} (pdftron-utils/get-pdftron-viewer viewer-key)]
    {:dispatch-n [[:analytics/save-datapoint
                   {:event event-name
                    :event-version 0
                    :ecid ecid
                    :viewer-key viewer-key
                    :current-page-index
                    (pdftron-utils/get-current-page-index apis)}]
                  [:pdftron-viewer/set-zoom
                   {:viewer-key viewer-key
                    :direction :in
                    :max-zoom-level max-zoom-level}]]}))

(kyber-event-fx :pdftron-viewer/zoom-in pdftron-zoom-in)

(when ^boolean js/goog.DEBUG
  (def event-doc-pdftron-zoom-out
    {:description
     "Zoom in event"
     :changelog
     [{:date "02MAR20"
       :event-version 0
       :data {:viewer-key :string
              :current-page-index :number}}]}))

(defn pdftron-zoom-out
  [_ [event-name {:keys [viewer-key min-zoom-level]}]]
  (let [{:keys [ecid apis]} (pdftron-utils/get-pdftron-viewer viewer-key)]
    {:dispatch-n [[:analytics/save-datapoint
                   {:event event-name
                    :event-version 0
                    :ecid ecid
                    :viewer-key viewer-key
                    :current-page-index
                    (pdftron-utils/get-current-page-index apis)}]
                  [:pdftron-viewer/set-zoom
                   {:viewer-key viewer-key
                    :direction :out
                    :min-zoom-level min-zoom-level}]]}))

(kyber-event-fx :pdftron-viewer/zoom-out pdftron-zoom-out)

(when ^boolean js/goog.DEBUG
  (def event-doc-pdftron-initialized
    {:description
     "PDFTron has been fully initialized"
     :changelog
     [{:date "13JAN20"
       :event-version 0
       :data {:viewer-key :string
              :current-page-index :number}}]}))

(defn pdftron-initialized
  [{:keys [db]} [event-name {:keys [options apis on-document-initialized]}]]
  (let [{:keys [ecid viewer-key]}
        options

        {:keys [has-crossed-percent-complete-threshold plo-type]}
        (:activity db)]

    {:db (assoc-in db
                   [:pdftron-instances viewer-key :zoom-level]
                   (js-invoke (:doc-viewer apis) "getZoom"))
     :dispatch-n (cond-> [[:analytics/save-datapoint
                           {:event event-name
                            :event-version 0
                            :ecid ecid
                            :viewer-key viewer-key
                            :current-page-index
                            (pdftron-utils/get-current-page-index apis)}]]
                   (some? on-document-initialized)
                   (conj on-document-initialized)

                   (and (= :main-activity viewer-key)
                        (= plo-type "pdf")
                        (true? has-crossed-percent-complete-threshold))
                   (conj [:pdftron-viewer/show-thumbnail-button
                          {:viewer-key viewer-key}]))
     :pdftron-viewer/set-viewer {:viewer-key viewer-key
                                 :ecid ecid
                                 :apis apis}}))


(kyber-event-fx :pdftron-viewer/pdftron-initialized pdftron-initialized)

(when ^boolean js/goog.DEBUG
  (def event-doc-initialize-pdftron
    {:description
     "Request to load a PDF document with pdftron"
     :changelog
     [{:date "09JAN20"
       :event-version 0
       :data {:viewer-key :string}}]}))

(defn initialize-pdftron
  [{:keys [db]} [event-name pdf options]]
  (let [url (or (:resource/url pdf)
                (:url pdf))

        {:keys [ecid viewer-key]}
        options

        formatted-url
        (str (base-uri db url) "?token=" (session-token db))

        user-sub
        (get-in db [:session :user :sub])

        initial-state
        (get-initial-pdftron-viewer-state options)]
    {:db (assoc-in db [:pdftron-instances viewer-key] initial-state)
     :dispatch-n [[:analytics/save-datapoint
                   {:event event-name
                    :event-version 0
                    :ecid ecid
                    :viewer-key viewer-key}]]
     :pdftron-viewer/load-document (assoc options
                                          :ecid ecid
                                          :url formatted-url
                                          :username user-sub)}))

(kyber-event-fx :pdftron-viewer/initialize-pdftron initialize-pdftron)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; PDF On Left Pannel Toggle
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-on-toggle-left-pannel
    {:description
     "PDF left pannel toggled"
     :changelog
     [{:date "12MAY20"
       :event-version 0
       :data {:is-visible :boolean
              :viewer-key :string
              :current-page-index :number}}]}))

(defn on-toggle-left-panel
  [{:keys [db]} [event-name _ _ {:keys [is-visible viewer-key]}]]
  (let [{:keys [ecid apis]}
        (pdftron-utils/get-pdftron-viewer viewer-key)]
    {:db (cond-> db
           (= is-visible false)
           (assoc-in [:pdftron-instances viewer-key :thumbnails-enabled] false))
     :dispatch-n [[:analytics/save-datapoint
                   {:ecid ecid
                    :event event-name
                    :event-version 0
                    :is-visible is-visible
                    :viewer-key viewer-key
                    :current-page-index
                    (pdftron-utils/get-current-page-index apis)}]]}))

(kyber-event-fx :pdftron-viewer/on-toggle-left-panel on-toggle-left-panel)


(when ^boolean js/goog.DEBUG
  (def event-doc-toggle-thumbnails
    {:description "Toggles thumbnail drawer"
     :changelog
     [{:date "02MAR20"
       :event-version 0
       :data {:state :string
              :viewer-key :string
              :current-page-index :number}}]}))

(defn toggle-thumbnails
  [{:keys [db]} [event-name {:keys [viewer-key]}]]
  (let [{:keys [ecid apis]}
        (pdftron-utils/get-pdftron-viewer viewer-key)

        {:keys [thumbnails-enabled]}
        (get-in db [:pdftron-instances viewer-key])

        next-state
        (not thumbnails-enabled)

        elements
        #js ["leftPanel" "thumbnailsPanel"]]

    (cond-> {:db (assoc-in db
                           [:pdftron-instances viewer-key :thumbnails-enabled]
                           next-state)
             :dispatch-n [[:analytics/save-datapoint
                           {:event event-name
                            :event-version 0
                            :ecid ecid
                            :state (if (true? next-state)
                                     :showing :hiding)
                            :viewer-key viewer-key
                            :current-page-index
                            (pdftron-utils/get-current-page-index apis)}]]}
      (true? next-state)
      (assoc :pdftron-viewer/show-elements {:apis apis
                                            :elements elements})

      (false? next-state)
      (assoc :pdftron-viewer/hide-elements {:apis apis
                                            :elements elements}))))

(kyber-event-fx :pdftron-viewer/toggle-thumbnails
                toggle-thumbnails)

(when ^boolean js/goog.DEBUG
  (def event-doc-toggle-search
    {:description "Toggles search drawer"
     :changelog
     [{:date "02MAR20"
       :event-version 0
       :data {:state :string
              :viewer-key :string
              :current-page-index :number}}]}))

(defn toggle-search
  [{:keys [db]} [event-name {:keys [viewer-key event-bus] :as options}]]
  (let [{:keys [on-toggle-search]}
        event-bus

        {:keys [ecid apis]}
        (pdftron-utils/get-pdftron-viewer viewer-key)

        {:keys [search-enabled]}
        (get-in db [:pdftron-instances viewer-key])

        next-state
        (not search-enabled)

        elements
        #js ["searchOverlay" "searchPanel"]]

    (cond-> {:db (assoc-in db
                           [:pdftron-instances viewer-key :search-enabled]
                           next-state)
             :dispatch-n [[:analytics/save-datapoint
                           {:event event-name
                            :event-version 0
                            :ecid ecid
                            :state (if (true? next-state)
                                     :showing :hiding)
                            :viewer-key viewer-key
                            :current-page-index
                            (pdftron-utils/get-current-page-index apis)}]]}
      (true? next-state)
      (assoc :pdftron-viewer/show-elements {:apis apis
                                            :elements elements})

      (false? next-state)
      (assoc :pdftron-viewer/hide-elements {:apis apis
                                            :elements elements})

      (some? on-toggle-search)
      (update :dispatch-n conj (into on-toggle-search [options
                                                       next-state])))))

(kyber-event-fx :pdftron-viewer/toggle-search
                toggle-search)

(when ^boolean js/goog.DEBUG
  (def event-doc-show-thumbnail-button
    {:description "Adds the thumbnail preview header icon to the header"
     :changelog
     [{:date "02MAR20"
       :event-version 0
       :data {:viewer-key :string
              :current-page-index :number}}]}))

(defn show-thumbnail-button
  [_ [event-name {:keys [viewer-key]}]]
  (let [{:keys [ecid apis]} (pdftron-utils/get-pdftron-viewer viewer-key)]
    {:dispatch-n [[:analytics/save-datapoint
                   {:event event-name
                    :event-version 0
                    :ecid ecid
                    :viewer-key viewer-key
                    :current-page-index
                    (pdftron-utils/get-current-page-index apis)}]]
     :pdftron-viewer/show-thumbnail-button {:apis apis}}))

(kyber-event-fx :pdftron-viewer/show-thumbnail-button
                show-thumbnail-button)

(when ^boolean js/goog.DEBUG
  (def event-doc-search-document
    {:description "Search event within a document"
     :changelog
     [{:date "16MAR20"
       :event-version 0
       :data {:viewer-key :string
              :current-page-index :number
              :search-term :string}}]}))

(defn search-document
  [_ [event-name {:keys [viewer-key search-term]}]]
  (let [{:keys [ecid apis]} (pdftron-utils/get-pdftron-viewer viewer-key)]
    {:dispatch-n [[:analytics/save-datapoint
                   {:event event-name
                    :event-version 0
                    :ecid ecid
                    :search-term search-term
                    :viewer-key viewer-key
                    :current-page-index
                    (pdftron-utils/get-current-page-index apis)}]]}))

(kyber-event-fx :pdftron-viewer/search-document
                search-document)
